import React, { useEffect, useMemo, useState } from "react";
import Navbar from "./components/Navbar";
import Hero from "./components/Hero";
import HeroMobile from "./components/HeroMobile";

import MobileShell from "./components/mobile/MobileShell";
import MobileHome from "./components/mobile/MobileHome";




import FloatingSponsor from "./components/FloatingSponsor";
import FloatingActions from "./components/FloatingActions";
import WhatsAppCoexistenceSignup from "./components/WhatsAppCoexistenceSignup";
import { captureAttribution } from "./analytics/attribution";
import {
  initializeAnalytics,
  trackContact,
  trackPageView,
  trackPhoneClick,
  trackViewContent,
} from "./analytics/analytics";

/* ----------------- Routes (hash-based, no extra deps) ----------------- */
const routes = {
  home: "#home",
  floors: "#floors",
  gallery: "#gallery",
  location: "#location",
  about: "#about",
  contact: "#contact",
};

function useHashRoute() {
  const [hash, setHash] = useState("#home");
  useEffect(() => {
    const getHash = () => (window?.location?.hash ? window.location.hash : "#home");
    setHash(getHash());
    const onHash = () => setHash(getHash());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);
  return hash || "#home";
}

/* ----------------- Config ----------------- */
const GRANADA = {
  address: "Abu Qash / Ramallah, Palestine",
  coords: { lat: 31.941417, lng: 35.191278 },
  email: "info@granada-palestine.com",
  phone: "+972 59-735-0458",
  floors: [
    { id: "B2",  title: { en: "Parking -2", enShort: "B2", ar: "موقف -2" }, img: "/images/floors/B2.png" },
    { id: "B1",  title: { en: "Parking -1", enShort: "B1", ar: "موقف -1" }, img: "/images/floors/B1.png" },
    { id: "R1",  title: { en: "Retail Level 1", enShort: "R1", ar: "المحلات 1", arShort: "مح1" }, img: "/images/floors/R1.png" },
    { id: "R2",  title: { en: "Retail Level 2", enShort: "R2", ar: "المحلات 2", arShort: "مح2" }, img: "/images/floors/R2.png" },
    { id: "G",   title: { en: "Ground — Retail", enShort: "G", ar: "الأرضي — محلات" }, img: "/images/floors/G.png" },
    { id: "L1",  title: { en: "Level 1 — Offices", enShort: "L1", ar: "الطابق 1 — مكاتب" }, img: "/images/floors/L1.png" },
    { id: "L2",  title: { en: "Level 2 — Offices", enShort: "L2", ar: "الطابق 2 — مكاتب" }, img: "/images/floors/L2.png" },
    { id: "L3",  title: { en: "Level 3 — Offices", enShort: "L3", ar: "الطابق 3 — مكاتب" }, img: "/images/floors/L3.png" },
    { id: "L4",  title: { en: "Level 4 — Offices", enShort: "L4", ar: "الطابق 4 — مكاتب" }, img: "/images/floors/L4.png" },
    { id: "L5",  title: { en: "Level 5 — Apartments", enShort: "L5", ar: "الطابق 5 — شقق" }, img: "/images/floors/L5.png" },
    { id: "RF1", title: { en: "Roof Level 1", enShort: "RF1", ar: "السطح 1", arShort: "سطح 1" }, img: "/images/floors/RF1.png" },
    { id: "RF2", title: { en: "Roof Level 2", enShort: "RF2", ar: "السطح 2", arShort: "سطح 2" }, img: "/images/floors/RF2.png" },
  ],
  gallery: [],
};

/* ----------------- i18n (plain object, no libs) ----------------- */
const STR = {
  en: {
    nav: { home: "Home", floors: "Floor Plans", gallery: "Gallery", location: "Location", about: "About", contact: "Contact", toggle: "العربية" },
    heroTop: "CRAFTED WITH PRECISION",
    heroTitle: "A premier mixed-use destination",

    designedBy: "Designed by Khaled Aqad",
    ctaPrimary: "Explore Floors",
    ctaSecondary: "Get in touch",
    features: [
      { h: "Prime Location", p: "Frontage on Birzeit St., near the university." },
      { h: "Flexible Plates", p: "Efficient office floors, easily divisible." },
      { h: "Parking", p: "Two underground levels with direct lift access." },
    ],
    floorsTitle: "Floor Plans",
    floorsHint: "Preassigned plans & photos per floor.",
    galleryTitle: "Gallery",
    galleryEmpty: "No images yet.",
    locationTitle: "Location",
    aboutTitle: "About",
    aboutText: "Granada combines street-level retail, efficient office floors, and upper-level residences with durable materials and clear circulation.",
    contactTitle: "Contact",
    contactForm: { name: "Name", email: "Email", message: "Message", send: "Send" },
    footerAddr: GRANADA.address,
    copyright: (y) => `© ${y} Granada`,
  },
  ar: {
    nav: { home: "الرئيسية", floors: "المخططات", gallery: "المعرض", location: "الموقع", about: "عن المشروع", contact: "اتصل بنا", toggle: "English" },
    heroTop: "صُنِعَ بإتقان",
    heroTitle: "واجهة متعددة الاستخدامات",
    designedBy: "تصميم: خالد عقّاد",
    ctaPrimary: "استعرض الطوابق",
    ctaSecondary: "تواصل معنا",
    features: [
      { h: "موقع مميز", p: "واجهة على شارع بيرزيت وبالقرب من الجامعة." },
      { h: "مساحات مرنة", p: "طوابق مكتبية فعالة قابلة للتقسيم." },
      { h: "مواقف سيارات", p: "طابقان تحت الأرض مع وصول مباشر للمصعد." },
    ],
    floorsTitle: "المخططات",
    floorsHint: "صور ومخططات ثابتة لكل طابق.",
    galleryTitle: "المعرض",
    galleryEmpty: "لا توجد صور حتى الآن.",
    locationTitle: "الموقع",
    aboutTitle: "عن المشروع",
    aboutText: "يجمع غرناطة بين محلات على مستوى الشارع وطوابق مكاتب فعالة وشقق علوية بمواد متينة وحركة واضحة.",
    contactTitle: "اتصل بنا",
    contactForm: { name: "الاسم", email: "البريد الإلكتروني", message: "الرسالة", send: "إرسال" },
    footerAddr: GRANADA.address,
    copyright: (y) => `© ${y} غرناطة`,
  },
};

/* ----------------- Style tokens ----------------- */
const UI = {
  teal: "#008b8b",
  border: "#e6e8eb",
  text: "#111",
  sub: "#5b6570",
  // Granada palette
  garnet: "#8B1E3F",  // deep crimson (headline / primary)
  emerald: "#1A6840", // accent
  ochre:  "#C49A6C",  // small caps / highlight
  ivory:  "#F4E6C1",  // subtitle text
};


/* ----------------- Helpers ----------------- */
const Container = ({ children, style }) => (
  <section style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px", ...style }}>
    {children}
  </section>
);

/* ----------------- Hooks: parallax & reveal ----------------- */
function useParallax(speed = 0.3) {
  const [y, setY] = useState(0);
  useEffect(() => {
    const onScroll = () => setY(window.scrollY * speed);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [speed]);
  return y;
}

function useReveal() {
  const ref = React.useRef(null);
  const [show, setShow] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setShow(true)),
      { rootMargin: "0px 0px -10% 0px", threshold: 0.1 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return { ref, show };
}

/* ----------------- Util: window width ----------------- */
function useWindowWidth() {
  const [w, setW] = useState(typeof window !== "undefined" ? window.innerWidth : 1024);
  useEffect(() => {
    const onResize = () => setW(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return w;
}

/* ----------------- Home (parallax hero + reveals) ----------------- */
function Home({ t, dir }) {
  //const heroImages = ["/images/hero1.jpg", "/images/hero2.jpg", "/images/hero3.jpg"];
  //const [currentImage, setCurrentImage] = useState(0);

  const width = useWindowWidth();
  const isSmall = width < 640;
  //const heroMinH = isSmall ? "70vh" : "60vh";
  const heroMinH = isSmall ? "70vh" : "60vh"; // (still used by sections below if you want)

// desktop Hero handles its own slideshow; mobile hero is static for perf

  const shift = useParallax(0.25);
  const feat = useReveal();

  return (
    <>
      {isSmall ? (
        <HeroMobile
          t={t}
          dir={dir}
          routes={routes}
         UI={UI}
          bg="/images/hero2.jpg"          // pick your best vertical-friendly image
          star="/images/star.svg"    // your decorative star
          showStar={true}
        />
      ) : (
        <Hero t={t} dir={dir} routes={routes} UI={UI} showArabesque={true} />
      )}

      <section
        ref={feat.ref}
        style={{
          background: "#fff",
          transition: "opacity .6s ease, transform .6s ease",
          opacity: feat.show ? 1 : 0,
          transform: feat.show ? "translateY(0px)" : "translateY(10px)",
        }}
      >
        <Container
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 16,
            padding: "24px 0 8px",
          }}
        >
          {[
            { h: dir === "rtl" ? "المساحة الإجمالية" : "Total Area", p: "≈ 4,800 m² (GFA)" },
            { h: dir === "rtl" ? "عدد المحلات" : "Retail Units", p: "G, R1, R2" },
            { h: dir === "rtl" ? "مواقف السيارات" : "Parking", p: "B1 & B2 (direct lift)" },
            { h: dir === "rtl" ? "المكاتب" : "Offices", p: "L1–L4 (flexible plates)" },
          ].map((it, i) => (
            <div key={i} style={{ border: "1px solid #e6e8eb", borderRadius: 14, padding: 18, background: "#fff", color: "#111" }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>{it.h}</div>
              <div style={{ color: UI.sub }}>{it.p}</div>
            </div>
          ))}
        </Container>
      </section>

      <section style={{ background: "#fff" }}>
        <Container
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 16,
            padding: "16px 0 40px",
          }}
        >
          {t.features.map((it, i) => (
            <div key={i} style={{ border: "1px solid #e6e8eb", borderRadius: 14, padding: 20, background: "#fff", color: "#111" }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>{it.h}</div>
              <div style={{ color: UI.sub }}>{it.p}</div>
            </div>
          ))}
        </Container>
      </section>
    </>
  );
}

/* ----------------- Page Header ----------------- */
function PageHeader({ title, subtitle }) {
  const HERO_URL = "/images/hero1.jpg";
  return (
    <section
      style={{
        backgroundImage: `linear-gradient(rgba(0,139,139,0.50), rgba(0,139,139,0.50)), url('${HERO_URL}')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        color: "#fff",
        minHeight: "36vh",
        display: "flex",
        alignItems: "flex-end",
      }}
    >
      <Container style={{ padding: "48px 0" }}>
        <h1 style={{ fontSize: 38, margin: 0, lineHeight: 1.1 }}>{title}</h1>
        {subtitle ? <p style={{ margin: "8px 0 0", opacity: 0.95, maxWidth: 800 }}>{subtitle}</p> : null}
      </Container>
    </section>
  );
}

/* ----------------- Floors ----------------- */
function FloorPlans({ t, lang, onFloorChange }) {
  const [active, setActive] = useState(GRANADA.floors[0]?.id || "");

  const images = useMemo(() => {
    const map = {};
    for (const f of GRANADA.floors) map[f.id] = f.img ? [f.img] : [];
    return map;
  }, []);

  const [selectedByFloor, setSelectedByFloor] = useState(() => {
    const first = GRANADA.floors[0]?.id;
    return first ? { [first]: images[first]?.[0] || "" } : {};
  });

  useEffect(() => {
    setSelectedByFloor((prev) => ({ ...prev, [active]: images[active]?.[0] || "" }));
  }, [active, images]);

  const selectFloor = (floorId) => {
    setActive(floorId);
    onFloorChange(floorId);
    trackViewContent({ content_id: `floor-${floorId.toLowerCase()}`, floor_number: floorId, content_type: "floor_plan", route: routes.floors, interaction: "select" });
  };

  const selected = selectedByFloor[active] || "";

  return (
    <>
      <PageHeader title={t.floorsTitle} subtitle={t.floorsHint} />
      <section style={{ padding: "32px 0 48px", background: "#fff" }}>
        <Container>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", margin: "0 0 16px", flexDirection: lang === "ar" ? "row-reverse" : "row", overflowX: "auto" }}>
            {GRANADA.floors.map((f) => {
              const fullTitle = f.title[lang] || f.title.en;
              const shortKey = lang === "ar" ? "arShort" : "enShort";
              const title = f.title[shortKey] || fullTitle;
              const isActive = active === f.id;
              return (
                <button
                  key={f.id}
                  onClick={() => selectFloor(f.id)}
                  title={fullTitle}
                  aria-pressed={isActive}
                  style={{
                    borderRadius: 999,
                    border: `1px solid ${UI.teal}`,
                    background: isActive ? UI.teal : "transparent",
                    color: isActive ? "#fff" : UI.teal,
                    padding: "8px 14px",
                    cursor: "pointer",
                    fontSize: 13,
                    whiteSpace: "nowrap",
                  }}
                >
                  {title}
                </button>
              );
            })}
          </div>

          {selected ? (
            <div
              onClick={() => {
                onFloorChange(active);
                trackViewContent({ content_id: `floor-${active.toLowerCase()}`, floor_number: active, content_type: "floor_plan", route: routes.floors, interaction: "lightbox" });
                document.getElementById("lightbox")?.showModal();
              }}
              style={{ border: `1px solid ${UI.border}`, borderRadius: 14, padding: 12, background: "#fff", marginBottom: 14, cursor: "zoom-in" }}
            >
              <div style={{ width: "100%", height: "60vh", display: "grid", placeItems: "center" }}>
                <img src={selected} alt={`${active}-preview`} loading="lazy" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
              </div>
            </div>
          ) : null}

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 12 }}>
            {(images[active] || []).length === 0 && (
              <div style={{ height: 160, borderRadius: 12, display: "grid", placeItems: "center", color: UI.sub, border: `1px solid ${UI.border}`, background: "#fff", fontSize: 13 }}>
                {lang === "ar" ? "لا توجد صور لهذا الطابق." : "No images for this floor."}
              </div>
            )}

            {(images[active] || []).map((src, i) => (
              <button
                key={i}
                onClick={() => {
                  onFloorChange(active);
                  setSelectedByFloor((prev) => ({ ...prev, [active]: src }));
                }}
                style={{ aspectRatio: "3/2", borderRadius: 12, overflow: "hidden", border: `2px solid ${selected === src ? UI.teal : UI.border}`, background: "#fff", padding: 0, cursor: "pointer" }}
              >
                <img src={src} alt={`floor-${active}-${i}`} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
              </button>
            ))}
          </div>
        </Container>
      </section>

      <dialog id="lightbox" style={{ border: "none", padding: 0, background: "transparent" }} onClick={(e) => e.currentTarget.close()}>
        <div style={{ width: "min(96vw, 1400px)", height: "min(92vh, 92vh)", background: "#000", borderRadius: 14, overflow: "hidden", border: "1px solid rgba(255,255,255,0.2)", display: "grid", placeItems: "center" }}>
          <img src={selected} alt="full" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
        </div>
      </dialog>
    </>
  );
}

/* ----------------- Gallery ----------------- */
function Gallery({ t }) {
  const imgs = GRANADA.gallery;
  return (
    <>
      <PageHeader title={t.galleryTitle} />
      <section style={{ padding: "32px 0 48px", background: "#fff" }}>
        <Container>
          {!imgs || imgs.length === 0 ? (
            <div style={{ border: `1px solid ${UI.border}`, background: "#fff", color: UI.sub, borderRadius: 12, padding: 16, textAlign: "center" }}>{t.galleryEmpty}</div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 12 }}>
              {imgs.map((src, i) => (
                <div key={i} style={{ aspectRatio: "4/3", borderRadius: 12, overflow: "hidden", border: `1px solid ${UI.border}`, background: "#fff" }}>
                  <img src={src} alt={`gallery-${i}`} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                </div>
              ))}
            </div>
          )}
        </Container>
      </section>
    </>
  );
}

/* ----------------- Location (map + landmarks) ----------------- */
/* ----------------- Location (map + landmarks) ----------------- */
function Location({ t, lang }) {
  // Landmarks with coords so we can build both map links
  const items = [
    { n: lang === "ar" ? "جامعة بيرزيت" : "Birzeit University", d: "≈ 5–7 min",  lat: 31.9583, lng: 35.1824 },
    { n: lang === "ar" ? "ميدان المنارة" : "Al-Manara Square",   d: "≈ 12–15 min", lat: 31.9021, lng: 35.1959 },
    { n: lang === "ar" ? "برج الاتصالات" : "Paltel Tower",       d: "≈ 10–12 min", lat: 31.9047, lng: 35.1904 },
    { n: `🏬 ${lang === "ar" ? "مزايــا مول" : "Mazaya Mall"}`,   d: "≈ 1–2 min",  lat: 31.908611, lng: 35.196694 },
    { n: `🏬 ${lang === "ar" ? "لاندمارك مول" : "ICON Mall"}`, d: "≈ 1–2 min", lat: 31.908611, lng: 35.196694 },
  ];

  return (
    <>
      <PageHeader title={t.locationTitle} subtitle={GRANADA.address} />
      <section style={{ padding: "32px 0 48px", background: "#fff" }}>
        <Container>
          {/* Project map */}
          <div style={{ border: `1px solid ${UI.border}`, borderRadius: 16, overflow: "hidden" }}>
            <iframe
              title="Granada Location"
              src={`https://www.google.com/maps?q=${GRANADA.coords.lat},${GRANADA.coords.lng}&output=embed`}
              style={{ width: "100%", height: 420, border: 0 }}
              loading="lazy"
              allowFullScreen
            />
          </div>

          {/* Open Granada in Maps */}
          <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${GRANADA.coords.lat},${GRANADA.coords.lng}`}
              target="_blank" rel="noreferrer"
              style={{ border: "1px solid #e6e8eb", borderRadius: 10, padding: "8px 12px", textDecoration: "none", color: "#111" }}
            >
              {lang === "ar" ? "افتح في خرائط Google" : "Open in Google Maps"}
            </a>
            <a
              href={`http://maps.apple.com/?ll=${GRANADA.coords.lat},${GRANADA.coords.lng}`}
              target="_blank" rel="noreferrer"
              style={{ border: "1px solid #e6e8eb", borderRadius: 10, padding: "8px 12px", textDecoration: "none", color: "#111" }}
            >
              {lang === "ar" ? "افتح في خرائط Apple" : "Open in Apple Maps"}
            </a>
          </div>

          {/* Nearby landmarks — each with Google & Apple buttons */}
          <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
            {items.map((it, i) => (
              <div key={i} style={{ border: `1px solid ${UI.border}`, borderRadius: 12, padding: 12, background: "#fff" }}>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>{it.n}</div>
                <div style={{ color: UI.sub, fontSize: 13, marginBottom: 8 }}>{it.d}</div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${it.lat},${it.lng}`}
                    target="_blank" rel="noreferrer"
                    style={{ border: "1px solid #e6e8eb", borderRadius: 8, padding: "4px 8px", fontSize: 12, textDecoration: "none", color: "#111" }}
                  >
                    {lang === "ar" ? "خرائط Google" : "Google Maps"}
                  </a>
                  <a
                    href={`http://maps.apple.com/?ll=${it.lat},${it.lng}`}
                    target="_blank" rel="noreferrer"
                    style={{ border: "1px solid #e6e8eb", borderRadius: 8, padding: "4px 8px", fontSize: 12, textDecoration: "none", color: "#111" }}
                  >
                    {lang === "ar" ? "خرائط Apple" : "Apple Maps"}
                  </a>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>
    </>
  );
}



/* ----------------- About ----------------- */
function About({ t }) {
  return (
    <>
      <PageHeader title={t.aboutTitle} />
      <section style={{ padding: "32px 0 48px", background: "#fff" }}>
        <Container>
          <p style={{ color: UI.sub }}>{t.aboutText}</p>
        </Container>
      </section>
    </>
  );
}

/* ----------------- Contact ----------------- */
function Contact({ t }) {
  const f = t.contactForm;
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");

  const onSubmit = (e) => {
    e.preventDefault();
    trackContact({ channel: "email", route: routes.contact, cta_location: "contact_form" });
    const body = encodeURIComponent(`${message}

— ${name} (${email})`);
    window.location.href = `mailto:${GRANADA.email}?subject=Granada%20Inquiry&body=${body}`;
  };

  return (
    <>
      <PageHeader title={t.contactTitle} />
      <section style={{ padding: "32px 0 48px", background: "#fff" }}>
        <Container>
          <form onSubmit={onSubmit} style={{ display: "grid", gap: 12, maxWidth: 720 }}>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder={f.name} style={{ padding: "12px 14px", borderRadius: 12, border: `1px solid ${UI.border}` }} />
            <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder={f.email} style={{ padding: "12px 14px", borderRadius: 12, border: `1px solid ${UI.border}` }} />
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder={f.message} style={{ padding: "12px 14px", height: 120, borderRadius: 12, border: `1px solid ${UI.border}` }} />
            <div>
              <button type="submit" style={{ background: UI.teal, color: "#fff", padding: "12px 16px", borderRadius: 10, border: 0, cursor: "pointer" }}>{f.send}</button>
            </div>
          </form>
          <div style={{ marginTop: 10, color: UI.sub, fontSize: 14 }}>
            <a href={`tel:${GRANADA.phone}`} onClick={() => {
              trackPhoneClick({ route: routes.contact, cta_location: "contact_details" });
              trackContact({ channel: "phone", route: routes.contact, cta_location: "contact_details" });
            }} style={{ color: UI.sub, textDecoration: "none" }}>{GRANADA.phone}</a>
            <span style={{ margin: "0 8px" }}>|</span>
            <a href={`mailto:${GRANADA.email}`} onClick={() => trackContact({ channel: "email", route: routes.contact, cta_location: "contact_details" })} style={{ color: UI.sub, textDecoration: "none" }}>{GRANADA.email}</a>
          </div>
        </Container>
      </section>
    </>
  );
}

/* ----------------- AI Assistant (floating) ----------------- */
function AIAssistant({ lang }) {
  const [open, setOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [input, setInput] = React.useState("");
  const [messages, setMessages] = React.useState([
    { role: "assistant", content: lang === "ar" ? "مرحباً! أنا مساعد غرناطة. اسألني عن العنوان، المخططات، الطوابق، أو كيفية التواصل." : "Hi! I'm the Granada assistant. Ask me about the address, floor plans, levels, or how to get in touch." }
  ]);

  const buildContext = () => {
    const floors = GRANADA.floors.map((f) => f.title[lang] || f.title.enShort || f.id).join(", ");
    return [`Address: ${GRANADA.address}`, `Phone: ${GRANADA.phone}`, `Email: ${GRANADA.email}`, `Floors: ${floors}`, `Features: ${(STR[lang].features || []).map((x) => x.h).join(", ")}`].join("\n");
  };

  const localAnswer = (qRaw) => {
    const q = (qRaw || "").toLowerCase();
    if (!q.trim()) return null;
    if (/(address|location|وين|الموقع|عنوان)/.test(q)) return GRANADA.address;
    if (/(phone|call|tel|هاتف|اتصال)/.test(q)) return `${GRANADA.phone}`;
    if (/(email|mail|بريد)/.test(q)) return `${GRANADA.email}`;
    if (/(floor|طابق|plans|مخطط|levels?|b\\d|l\\d|r\\d|g|rf\\d)/.test(q)) {
      const list = GRANADA.floors.map((f) => `${f.id} — ${f.title[lang] || f.title.en}`).join("\n");
      return (lang === "ar" ? "هذه قائمة الطوابق المتاحة:\n" : "Here are the available floors:\n") + list;
    }
    if (/(parking|مواقف)/.test(q)) return lang === "ar" ? "يوجد موقفان تحت الأرض: B1 و B2 مع وصول مباشر للمصعد." : "There are two underground parking levels: B1 and B2 with direct lift access.";
    if (/(office|مكتب|offices)/.test(q)) return lang === "ar" ? "الطوابق L1–L4 مخصصة للمكاتب ومساحاتها قابلة للتقسيم." : "Levels L1–L4 are for offices with flexible plates.";
    if (/(retail|محلات)/.test(q)) return lang === "ar" ? "المحلات في G و R1 و R2." : "Retail levels are G, R1 and R2.";
    if (/(apartments|شقق)/.test(q)) return lang === "ar" ? "الشقق في الطابق L5." : "Apartments are on level L5.";
    if (/(roof|سطح)/.test(q)) return lang === "ar" ? "يوجد مستويان للسطح: RF1 و RF2." : "There are two roof levels: RF1 and RF2.";
    return null;
  };

  const askServer = async (question) => {
    try {
      const res = await fetch("/api/ask", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ lang, question, context: buildContext() }) });
      if (!res.ok) throw new Error("HTTP " + res.status);
      const data = await res.json();
      return data?.answer?.trim();
    } catch {
      return null;
    }
  };

  const onSend = async (e) => {
    e?.preventDefault?.();
    if (!input.trim() || busy) return;
    const q = input.trim();
    setMessages((m) => [...m, { role: "user", content: q }]);
    setInput("");
    setBusy(true);

    let answer = await askServer(q);
    if (!answer) answer = localAnswer(q);
    if (!answer) {
      answer = lang === "ar" ? `لم أجد إجابة دقيقة. يمكنك الاتصال بنا على ${GRANADA.phone} أو ${GRANADA.email}.` : `I couldn't find an exact answer. You can reach us at ${GRANADA.phone} or ${GRANADA.email}.`;
    }
    setMessages((m) => [...m, { role: "assistant", content: answer }]);
    setBusy(false);
  };

  return (
    <>
      <button onClick={() => setOpen(!open)} aria-expanded={open} style={{ position: "fixed", right: 18, bottom: 18, zIndex: 60, borderRadius: 999, background: UI.teal, color: "#fff", border: "none", padding: "12px 16px", boxShadow: "0 8px 24px rgba(0,0,0,0.18)", cursor: "pointer" }}>
        {open ? (lang === "ar" ? "إغلاق المساعد" : "Close Assistant") : (lang === "ar" ? "اسأل عن غرناطة" : "Ask about Granada")}
      </button>

      {open && (
        <div role="dialog" aria-label={lang === "ar" ? "مساعد غرناطة" : "Granada Assistant"} style={{ position: "fixed", right: 18, bottom: 74, width: 340, maxHeight: "60vh", background: "#fff", border: `1px solid ${UI.border}`, borderRadius: 14, overflow: "hidden", boxShadow: "0 16px 48px rgba(0,0,0,0.22)", zIndex: 60, display: "flex", flexDirection: "column" }}>
          <div style={{ background: "rgba(0,139,139,0.08)", padding: 10, borderBottom: `1px solid ${UI.border}` }}>
            <strong>{lang === "ar" ? "مساعد غرناطة" : "Granada Assistant"}</strong>
          </div>
          <div style={{ padding: 10, display: "flex", flexDirection: "column", gap: 8, overflowY: "auto" }}>
            {messages.map((m, i) => (
              <div key={i} style={{ alignSelf: m.role === "user" ? "flex-end" : "flex-start", background: m.role === "user" ? "#e9f5f5" : "#f7f7f8", color: UI.text, padding: "8px 10px", borderRadius: 10, maxWidth: "85%", whiteSpace: "pre-wrap", lineHeight: 1.4 }}>
                {m.content}
              </div>
            ))}
          </div>
          <form onSubmit={onSend} style={{ borderTop: `1px solid ${UI.border}`, padding: 8, display: "flex", gap: 6 }}>
            <input value={input} onChange={(e) => setInput(e.target.value)} placeholder={lang === "ar" ? "اسأل عن الطوابق أو الموقع..." : "Ask about floors or location..."} style={{ flex: 1, padding: "10px 12px", borderRadius: 10, border: `1px solid ${UI.border}` }} />
            <button type="submit" disabled={busy} style={{ background: UI.teal, color: "#fff", border: 0, borderRadius: 10, padding: "10px 12px", cursor: "pointer" }}>
              {busy ? (lang === "ar" ? "...يرد" : "...replying") : (lang === "ar" ? "إرسال" : "Send")}
            </button>
          </form>
          <div style={{ padding: 8, fontSize: 11, color: UI.sub, textAlign: "center" }}>{lang === "ar" ? "تنبيه: الأجوبة لأغراض معلوماتية فقط." : "Note: Answers are for informational purposes only."}</div>
        </div>
      )}
    </>
  );
}

/* ----------------- App ----------------- */
function GranadaSiteApp() {
  const [selectedFloor, setSelectedFloor] = useState("");
  const initialRoute = React.useRef(true);
  const [lang, setLang] = useState(() => {
    try {
      return (
        localStorage.getItem("granada_lang") ||
        ((navigator.language || "").startsWith("ar") ? "ar" : "en")
      );
    } catch {
      return (navigator.language || "").startsWith("ar") ? "ar" : "en";
    }
  });

  useEffect(() => {
    document.title = lang === "ar" ? "غرناطة" : "Granada";
    document.documentElement.lang = lang === "ar" ? "ar" : "en";
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
  }, [lang]);

  const t = STR[lang];
  const dir = lang === "ar" ? "rtl" : "ltr";
  const hash = useHashRoute();

  useEffect(() => {
    captureAttribution();
    initializeAnalytics();
  }, []);

  useEffect(() => {
    trackPageView({ route: hash, language: lang, initial: initialRoute.current });
    initialRoute.current = false;
    if (hash === routes.floors) {
      trackViewContent({ content_id: "floors", content_type: "floor_plans", route: hash, interaction: "section_view" });
    }
  }, [hash, lang]);

  // responsive: switch live on resize/rotation
  const w = useWindowWidth();
  const isSmall = w < 640;

  const Page = useMemo(() => {
    switch (hash) {
      case routes.floors:
        return <FloorPlans t={t} lang={lang} onFloorChange={setSelectedFloor} />;
      case routes.gallery:
        return <Gallery t={t} />;
      case routes.location:
        return <Location t={t} lang={lang} />;
      case routes.about:
        return <About t={t} />;
      case routes.contact:
        return <Contact t={t} />;
      default:
        // unique mobile home vs desktop home
        return isSmall ? (

          <MobileHome t={t} dir={dir} routes={routes} UI={UI} lang={lang} setLang={setLang} />
        ) : (
          <Home t={t} dir={dir} />
        );
    }
  }, [hash, t, lang, dir, isSmall]);

  // --- Mobile frame (app-like) ---
  if (isSmall) {
    return (
      <div
        dir={dir}
        style={{
          fontFamily:
            lang === "ar"
              ? "Tajawal, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif"
              : "Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
          color: UI.text,
        }}
      >
        <MobileShell
          t={t}
          lang={lang}
          setLang={setLang}
          dir={dir}
          routes={routes}
          UI={UI}
        >
          {Page}
        </MobileShell>
        <FloatingActions
          phone={GRANADA.phone}
          lang={lang}
          route={hash}
          selectedFloor={selectedFloor}
          bottom="calc(env(safe-area-inset-bottom, 0px) + 82px)"
        />
      </div>
    );
  }

  // --- Desktop frame (unchanged) ---
  return (
    <div
      dir={dir}
      style={{
        fontFamily:
          lang === "ar"
            ? "Tajawal, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif"
            : "Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
        color: UI.text,
      }}
    >
      <main id="main">
        <Navbar t={t} lang={lang} setLang={setLang} dir={dir} hash={hash} />
        {Page}
      </main>

      <footer style={{ borderTop: `1px solid ${UI.border}`, marginTop: 24 }}>
        <Container
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "18px 0",
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <div style={{ color: UI.sub }}>{t.footerAddr}</div>
          <div style={{ color: UI.sub }}>
            <a href="/privacy-policy/" style={{ color: UI.sub, marginInlineEnd: 14 }}>
              {lang === "ar" ? "سياسة الخصوصية" : "Privacy Policy"}
            </a>
            {t.copyright(new Date().getFullYear())}
          </div>
        </Container>
      </footer>

      <FloatingActions phone={GRANADA.phone} lang={lang} route={hash} selectedFloor={selectedFloor} />
      <AIAssistant lang={lang} />
      <FloatingSponsor lang={lang} />
    </div>
  );
}

export default function App() {
  if (window.location.pathname === "/meta-embedded-signup" || window.location.pathname === "/meta-embedded-signup/") {
    return <WhatsAppCoexistenceSignup />;
  }
  return <GranadaSiteApp />;
}
